import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, {
    FC,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Menu, Alert, Button, FormBuilder, Space, Empty, Spin } from 'udesk-ui';
import InspectionDetail from 'src/components/pages/components/inspection-detail';
import UdeskPagination from 'Component/common/udesk-pagination';
import UdeskDatePicker from 'Component/common/udesk-date-picker';
import type { FormProps } from 'udesk-ui/lib/form-builder/types/form';
import 'udesk-ui/lib/form/style/index.less';
import Card from '../Card';
import GradedFilter from '../GradedFilter';
import RatingFilter from '../RatingFilter';
import { AppealConfigModeMap } from 'src/pages/tasks/manage/template/automatic-add/components/business-config/const';
import isNil from 'lodash/isNil';
import moment from 'moment';

let timer: any = null;
let timer2: any = null;
let timer1: any = null;

interface ComplainButton {
    complainedCount?: number;

    inputReason?: number;

    isComplainValid?: number;

    permitComplainCount?: number;

    permitComplainDateTime?: string;

    show?: number;

    surplusComplainCount?: number;

    complainPeriodMode?: number;

    complainDeadCount?: number;

    complainDeadTime?: string;

    inspectResultComplainTextShow?: number;
}

interface ComplainPoint {
    complainReason?: string;

    complainType?: number;

    pointId?: number;
}

interface UserPermission {
    permissions?: string[];
}

interface TemplateProps {
    sdkOptions: any;
    forwardRef?: any;
    tasksType: number;
    setTasksType: (tasksType: number) => void;
    setQualityTasksTypes: (qualityTasksTypes: any[]) => void;
    loading: boolean;
    setLoading: (loading: boolean) => void;
}

const EMBER_ROUTER_ROOT = '/ember';

const SDK_VIEW_ALL_PERMISSION = 'sdk:viewAll';
const SDK_REVIEW_PERMISSION = 'sdk:review';

const hasSdkPermission = (permissions: string[] = [], permissionCode: string): boolean =>
    permissions.includes(permissionCode);

const getCurrentAgentEmail = (sdkOptions: any): string =>
    sdkOptions?.props?.agent ||
    (Udesk as any).data?.init?.sdkParams?.agent ||
    (Udesk as any).data?.init?.user?.email ||
    '';

const getReviewDetailPath = (taskId: number, reviewId: number, inspectionId: number): string => {
    const fallbackPath = `/site/tasks/manage/${taskId}/workbench/review-list/detail/${reviewId}`;
    let path = '';

    try {
        path = Udesk.ui.routing.getRoutePath({
            routeName: 'reviewListDetail',
            pathParams: {
                taskId,
                id: reviewId,
            },
        });
    } catch (error) {
        path = fallbackPath;
    }

    if (!path || path === '/' || path.includes(':taskId') || path.includes(':id')) {
        path = fallbackPath;
    }

    return `${path}?inspectionId=${inspectionId}`;
};

const Template: FC<TemplateProps> = memo((props) => {
    const {
        sdkOptions,
        forwardRef,
        tasksType,
        setTasksType,
        setQualityTasksTypes,
        loading,
        setLoading,
    } = props;
    const locales = UdeskLocales['current'];
    const appealLocales = locales.components.pages.appealSdk;

    const [formRef] = FormBuilder.useForm();
    const [selectedTaskId, setSelectedTaskId] = useState<number>(0);
    // const [related, setRelated] = useState<boolean>(false);

    const [listLoading, setListLoading] = useState<boolean>(false);
    const [isInit, setIsInit] = useState<boolean>(true);
    const [dialogRemarkList, setDialogRemarkList] = useState<any[]>([]);
    const [detail, setDetail] = useState<any>({});
    // const [pointCategoryList, setPointCategoryList] = useState<any[]>([]);
    // const [callDetailsList, setCallDetailsList] = useState<any[]>([]);
    const [pointChildrenComputer, setPointChildrenComputer] = useState<any>({});
    const [pointChildrenPerson, setPointChildrenPerson] = useState<any>({});
    // const [complaintList, setComplaintList] = useState<any>([]);

    const [complainButton, setComplainButton] = useState<ComplainButton>({});
    const [complainPointList, setComplainPointList] = useState<ComplainPoint[]>([]);
    const [complaintHistory, setComplaintHistory] = useState<ComplainPoint[]>([]);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<any>({
        beginTime: '',
        endTime: '',
    });
    const [resetComplainPointIdMap, setResetComplainPointIdMap] = useState(0);
    const [paging, setPage] = useState<any>({
        pageNum: 1,
        pageSize: 20,
        total: 0,
    });
    const [taskList, setTaskList] = useState<any[]>([]);
    const [taskIdsOptions, setTaskIdsOptions] = useState<any[]>([]);
    const [agentOptions, setAgentOptions] = useState<any[]>([]);
    const [showHit, setShowHit] = useState<0 | 1>(0);
    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [userPermission, setUserPermission] = useState<UserPermission>({});
    const userPermissionsRef = useRef<string[]>([]);

    const isTicket = tasksType === Udesk.enums.inspectDataSources.ticket.id;
    const isMixedType = tasksType === Udesk.enums.inspectDataSources.mixedType.id;
    const isSurvey = tasksType === Udesk.enums.inspectDataSources.survey.id;
    const isVoiceCall = tasksType === Udesk.enums.inspectDataSources.voiceCall.id;
    const isTextDialogue = tasksType === Udesk.enums.inspectDataSources.textDialogue.id;
    const isFromSdk = sdkOptions?.props?.routerRoot === EMBER_ROUTER_ROOT;
    const userPermissions = useMemo(() => userPermission.permissions || [], [userPermission.permissions]);
    userPermissionsRef.current = userPermissions;
    const hasViewAllPermission = hasSdkPermission(userPermissions, SDK_VIEW_ALL_PERMISSION);
    const canReview = hasSdkPermission(userPermissions, SDK_REVIEW_PERMISSION);
    const canComplain = !canReview;
    const currentAgentEmail = getCurrentAgentEmail(sdkOptions);
    const agentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const currentAgentOptions = useMemo(
        () =>
            currentAgentEmail
                ? [{ label: currentAgentEmail, value: currentAgentEmail }]
                : [],
        [currentAgentEmail]
    );
    const displayedAgentOptions = hasViewAllPermission ? agentOptions : currentAgentOptions;

    useEffect(() => {
        if (hasViewAllPermission) {
            formRef.setFieldsValue({
                agents: sdkOptions?.props?.agents,
            });
            return;
        }

        if (currentAgentEmail) {
            formRef.setFieldsValue({
                agents: [currentAgentEmail],
            });
        }
    }, [currentAgentEmail, formRef, hasViewAllPermission, sdkOptions?.props?.agents]);

    useEffect(() => {
        return () => {
            if (agentTimerRef.current) {
                clearTimeout(agentTimerRef.current);
            }
        };
    }, []);

    const showHitChange = (showHit) => {
        setShowHit(showHit ? 1 : 0);
    };
    const handMarkMap = () => {};

    const onSelect = ({ key }: any) => {
        setSelectedTaskId(key * 1);
        changeReadStatus(key, taskList);
    };
    const changeReadStatus = useCallback(
        (key, taskList) => {
            const item = taskList.find((i) => i.dataId === key * 1);
            if (!item) return;
            if (item.fieldValueMap['f_2'] === Udesk.enums.appealCheckStatus.unread.name) {
                // let url = Udesk.business.apiPath.concatApiPath(`/open_api_v1/inspection/${task.dataId}/read`, sdkOptions);
                // Udesk.ajax.post(url)
                Udesk.business.sdkAjax
                    .post(`/open_api_v1/inspection/${item.dataId}/read`, sdkOptions, { props })
                    .then(
                        () => {
                            item.fieldValueMap['f_2'] = Udesk.enums.appealCheckStatus.readed.name;
                            setTaskList([...taskList]);
                        },
                        (reason) => {
                            Udesk.ui.notify.error(
                                reason.errorMsg ||
                                    /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                        .appealCheckingTask.sdk.index
                                        .interfaceRequestExceptionPleaseTryAgainLater
                            );
                        }
                    );
            }
        },
        [props, sdkOptions]
    );

    const getParams = useCallback(
        (searchParams) => {
            if (!searchParams) {
                searchParams = {};
            }
            if (Array.isArray(searchParams.time) && searchParams.time[0] && searchParams.time[1]) {
                searchParams.beginTime = searchParams.time[0];
                searchParams.endTime = searchParams.time[1];
            }
            if (
                Array.isArray(searchParams.inspectTime) &&
                searchParams.inspectTime[0] &&
                searchParams.inspectTime[1]
            ) {
                searchParams.inspectStartTime = searchParams.inspectTime[0];
                searchParams.inspectEndTime = searchParams.inspectTime[1];
            }
            if (
                Array.isArray(searchParams.sampleTime) &&
                searchParams.sampleTime[0] &&
                searchParams.sampleTime[1]
            ) {
                searchParams.sampleStartTime = searchParams.sampleTime[0];
                searchParams.sampleEndTime = searchParams.sampleTime[1];
            }
            // eslint-disable-next-line
            const { graded = {}, time: _time, ...otherParams } = searchParams;
            delete otherParams.inspectTime;
            delete otherParams.sampleTime;
            const latestHasViewAllPermission = hasSdkPermission(userPermissionsRef.current, SDK_VIEW_ALL_PERMISSION);
            const latestCurrentAgentEmail = getCurrentAgentEmail(sdkOptions);
            const agents = latestHasViewAllPermission
                ? otherParams.agents
                : latestCurrentAgentEmail
                ? [latestCurrentAgentEmail]
                : undefined;
            let params = {
                ...paging,
                inspectDataSource: tasksType,
                isFromSdk,
                ...otherParams,
                agents,
                ...graded,
            };
            let querryDateRange = dateRange;
            if (
                isInit &&
                sdkOptions?.props?.defaultDateRange &&
                sdkOptions?.props?.defaultDateRange.length
            ) {
                let dateRange = sdkOptions.props.defaultDateRange;
                querryDateRange = {
                    beginTime: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') + ' 00:00:00' : '',
                    endTime: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') + ' 23:59:59' : '',
                };
                setDateRange(querryDateRange);
                setIsInit(false);
                params = { ...params, ...querryDateRange };
            }
            return params;
        },
        [
            dateRange,
            isFromSdk,
            isInit,
            paging,
            sdkOptions,
            tasksType,
        ]
    );

    const searchTasks = useCallback(
        (listParams, cb) => {
            if (timer2) {
                clearTimeout(timer2);
            }

            timer2 = setTimeout(() => {
                Udesk.business.sdkAjax
                    .get('open_api_v1/task-list-v1', sdkOptions, { props }, listParams)
                    .then(
                        (resp) => {
                            // setTaskIdsOptions(resp.data?.map?.((i) => ({ label: i.name, value: i.id })) ?? []);
                            cb(resp);
                        },
                        (reason) => {
                            Udesk.ui.notify.error(
                                reason.errorMsg ||
                                    /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                        .appealCheckingTask.sdk.index
                                        .interfaceRequestExceptionPleaseTryAgainLater
                            );
                            // reject(reason);
                            // this.privates.loading = false;
                            // this.actions.update();
                        }
                    );
            }, 500);
        },
        [props, sdkOptions]
    );
    const searchTasksInFilter = (listParams, cb) => {
        if (timer1) {
            clearTimeout(timer1);
        }

        timer1 = setTimeout(() => {
            Udesk.business.sdkAjax
                .get('open_api_v1/task-list-v1', sdkOptions, { props }, listParams)
                .then(
                    (resp) => {
                        // setTaskIdsOptions(resp.data?.map?.((i) => ({ label: i.name, value: i.id })) ?? []);
                        cb(resp);
                    },
                    (reason) => {
                        Udesk.ui.notify.error(
                            reason.errorMsg ||
                                /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                    .appealCheckingTask.sdk.index
                                    .interfaceRequestExceptionPleaseTryAgainLater
                        );
                        // reject(reason);
                        // this.privates.loading = false;
                        // this.actions.update();
                    }
                );
        }, 500);
    };

    const setTaskIdsOptionsFromAjax = (resp) =>
        setTaskIdsOptions(resp.data?.map?.((i) => ({ label: i.name, value: i.id })) ?? []);

    const onSelectSearchTask = (value) => {
        searchTasks(
            {
                keywords: value,
                inspectDataSource: tasksType,
            },
            setTaskIdsOptionsFromAjax
        );
    };

    const searchAgents = useCallback(
        (keyword = '') => {
            if (agentTimerRef.current) {
                clearTimeout(agentTimerRef.current);
            }

            agentTimerRef.current = setTimeout(() => {
                Udesk.business.sdkAjax
                    .get('open_api_v1/agent/list-v1', sdkOptions, { props }, { keyword })
                    .then(
                        (resp) => {
                            setAgentOptions(
                                resp.data
                                    ?.filter?.((item) => item.agentEmail)
                                    ?.map?.((item) => ({
                                        label: `${item.agentName}(${item.agentEmail})`,
                                        value: item.agentEmail,
                                    })) ?? []
                            );
                        },
                        (reason) => {
                            Udesk.ui.notify.error(
                                reason.errorMsg ||
                                    /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                        .appealCheckingTask.sdk.index
                                        .interfaceRequestExceptionPleaseTryAgainLater
                            );
                        }
                    );
            }, 500);
        },
        [props, sdkOptions]
    );

    const onReview = useCallback((complaint?: any) => {
        const reviewId = complaint?.id;
        const inspectionId = complaint?.inspectionId || complaint?.inspectDataId || detail.id;
        const taskId = complaint?.taskId || detail.taskId;
        if (!reviewId || !inspectionId || !taskId) {
            Udesk.ui.notify.error(/* 未获取到复核详情信息 */UdeskLocales['current'].pages.appealCheckingTask.sdk.components.qaResultView.index.reviewDetailsNotObtained);
            return;
        }

        // 使用 window.open 在用户点击的同步回调中打开新标签页，不会被拦截
        window.open(getReviewDetailPath(taskId, reviewId, inspectionId));
    }, [detail]);

    const onExport = useCallback(() => {
        const values = formRef.getFieldsValue();
        const { rating, ...formValues } = values;
        // eslint-disable-next-line
        const { pageNum, pageSize, ...params } = getParams({
            ...formValues,
            ...rating,
        });

        setExportLoading(true);
        Udesk.business.sdkAjax
            .post('open_api_v1/export/inspection-data', sdkOptions, { props }, params)
            .then(
                () => {
                    Udesk.ui.notify.success(locales.labels.exportTaskCreated);
                },
                (reason) => {
                    Udesk.ui.notify.error(
                        reason.errorMsg ||
                            /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                .appealCheckingTask.sdk.index
                                .interfaceRequestExceptionPleaseTryAgainLater
                    );
                }
            )
            .finally(() => {
                setExportLoading(false);
            });
    }, [getParams, formRef, sdkOptions, props, locales]);

    const searchList = useCallback(
        (searchParams: any = {}, needResetSelectTaskId = false) => {
            const params = getParams(searchParams);
            setListLoading(true);
            Udesk.business.sdkAjax
                .post('open_api_v1/inspection/list', sdkOptions, { props }, params)
                .then(
                    (resp) => {
                        const newTaskList = resp.data?.fieldDataList || [];
                        setTaskList(newTaskList);
                        if ((resp.data?.fieldDataList?.length ?? 0) > 0) {
                            if (needResetSelectTaskId) {
                                setSelectedTaskId(resp.data.fieldDataList[0].dataId);
                                changeReadStatus(resp.data.fieldDataList[0].dataId, newTaskList);
                            }
                        } else {
                            setSelectedTaskId(0);
                        }
                        setPage(resp.paging);
                        setListLoading(false);
                    },
                    (reason) => {
                        Udesk.ui.notify.error(
                            reason.errorMsg ||
                                /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                    .appealCheckingTask.sdk.index
                                    .interfaceRequestExceptionPleaseTryAgainLater
                        );
                        setListLoading(false);
                    }
                );
        },
        [changeReadStatus, getParams, props, sdkOptions]
    );
    const searchListFromForm = (searchParams: any = {}) => {
        const { rating, ...params } = searchParams;
        searchList(
            {
                ...params,
                ...rating,
                pageNum: 1,
            },
            true
        );
    };
    // const searchComplaintList = (searchParams: any = {}) => {
    //     const params = getParams(searchParams);
    //     Udesk.business.sdkAjax.post('open_api_v1/complaint/list', sdkOptions, { props }, params).then(
    //         (resp) => {
    //             setComplaintList(resp.data?.fieldDataList || []);
    //         },
    //         (reason) => {
    //             Udesk.ui.notify.error(reason.errorMsg || '接口请求异常，请稍后再试');
    //         }
    //     );
    // };
    const pageChanged = (pageNum) => {
        setPage({
            ...paging,
            pageNum,
        });
        const values = formRef.getFieldsValue();
        searchList({
            pageNum,
            ...values,
        });
    };
    const onTasksTypeChange = useCallback(
        (activeKey) => {
            setTasksType(activeKey * 1);

            formRef.setFieldsValue({
                taskIds: undefined,
                callId: undefined,
                dialogId: undefined,
            });

            const values = formRef.getFieldsValue();

            setPage({
                ...paging,
                pageNum: 1,
            });
            searchList(
                {
                    inspectDataSource: activeKey * 1,
                    pageNum: 1,
                    ...values,
                },
                true
            );
            const listParams = {
                inspectDataSource: activeKey,
            };
            searchTasks(listParams, setTaskIdsOptionsFromAjax);
        },
        [formRef, paging, searchList, searchTasks, setTasksType]
    );

    const getComplainButton = () => {
        Udesk.business.sdkAjax
            .get(`open_api_v1/complain-button/${selectedTaskId}`, sdkOptions, { props })
            .then(
                (resp) => {
                    setComplainButton(resp.data || {});
                },
                (reason) => {
                    console.log(reason, 'reason');
                    Udesk.ui.notify.error(
                        reason.errorMsg ||
                            /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                .appealCheckingTask.sdk.index
                                .interfaceRequestExceptionPleaseTryAgainLater
                    );
                }
            );
    };
    const onComplainPointListChange = (complainPointList) => {
        setComplainPointList(complainPointList);
    };
    const getComplainHistory = () => {
        Udesk.business.sdkAjax
            .get(`open_api_v1/${selectedTaskId}/complaint/list`, sdkOptions, { props })
            .then(
                (resp) => {
                    setComplaintHistory(resp.data);
                },
                (reason) => {
                    Udesk.ui.notify.error(
                        reason.errorMsg ||
                            /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                .appealCheckingTask.sdk.index
                                .interfaceRequestExceptionPleaseTryAgainLater
                    );
                }
            );
    };
    const onSubmit = () => {
        if (!complainPointList?.length) {
            Udesk.ui.notify.error(locales.fix.appealError);
            return;
        }
        let params = {
            pointList: complainPointList,
        };
        // 这里判断是否是申诉全部
        const item = complainPointList.find((item) => typeof item.pointId === 'string');
        if (item) {
            const ids = item.pointId!.toString().split('/');
            params.pointList = ids.map((pointId) => {
                return {
                    ...item,
                    pointId: +pointId,
                    complainSubmitType: 'CALL',
                } as any;
            });
        }

        setSubmitLoading(true);

        Udesk.business.sdkAjax
            .post(
                `open_api_v1/inspection/${selectedTaskId}/complain`,
                sdkOptions,
                { props },
                params
            )
            .then(
                () => {
                    Udesk.ui.notify.success(appealLocales.complainSuccess);
                    getComplainButton();
                    getComplainHistory();
                    const formValues = formRef.getFieldsValue();
                    searchList(formValues);
                    setComplainPointList([]);
                    setSubmitLoading(false);
                    setResetComplainPointIdMap((prev) => prev + 1);
                },
                (reason) => {
                    Udesk.ui.notify.error(
                        reason.errorMsg ||
                            /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                .appealCheckingTask.sdk.index
                                .interfaceRequestExceptionPleaseTryAgainLater
                    );
                    setSubmitLoading(false);
                }
            );
    };
    const refreshList = () => {
        const formValues = formRef.getFieldsValue();
        searchList(formValues);
    };

    useEffect(() => {
        if (!selectedTaskId) return;
        setLoading(true);
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => {
            Udesk.business.sdkAjax
                .get(`open_api_v1/inspection/${selectedTaskId}`, sdkOptions, { props })
                .then(
                    (resp) => {
                        let pointChildrenComputer = {};
                        let pointChildrenPerson = {};
                        setSelectedTaskId(resp.data.id);
                        const {
                            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
                            relatedInspectionDataExtension = {},
                            // related,
                            ...detail
                        } = resp.data;
                        // const { related } = detail;
                        // setRelated(Boolean(related));
                        // const callDetailsList = Object.values(relatedInspectionDataExtension);
                        // if (related) {
                        //     setCallDetailsList(callDetailsList);
                        // } else {
                        //     setCallDetailsList([resp.data]);
                        // }
                        // let firstCallDetail: any = callDetailsList[0] || {};
                        // this.privates.activeCallId = `${firstCallDetail.callId}`;
                        //单句备注
                        setDialogRemarkList(detail.remarkList || []);
                        let task = { ...detail };
                        // let task = { ...detail, ...firstCallDetail };
                        //质检点（一级分类）列表
                        if (task.pointCategoryList && task.pointCategoryList.length > 0) {
                            task.pointCategoryList.forEach((item) => {
                                //一级分类下有二级分类
                                if (item.children) {
                                    item.children.forEach((items) => {
                                        //二级分类下有质检点
                                        if (items.pointDataList) {
                                            items.pointDataList.forEach((itemss) => {
                                                //质检点有人工标记的高亮（？不确定）
                                                if (itemss.handMarkHighlightSeqMap) {
                                                    if (itemss.handMarkHighlightSeqMap[1]) {
                                                        itemss.handMarkHighlightSeqMap[1].forEach(
                                                            (itemsss) => {
                                                                if (pointChildrenPerson[itemsss]) {
                                                                    pointChildrenPerson[
                                                                        itemsss
                                                                    ].push({
                                                                        id: itemss.pointId,
                                                                        name: itemss.name,
                                                                    });
                                                                } else {
                                                                    pointChildrenPerson = {
                                                                        ...pointChildrenPerson,
                                                                        [itemsss]: [
                                                                            {
                                                                                id: itemss.pointId,
                                                                                name: itemss.name,
                                                                            },
                                                                        ],
                                                                    };
                                                                }
                                                            }
                                                        );
                                                    }
                                                    if (itemss.handMarkHighlightSeqMap[2]) {
                                                        itemss.handMarkHighlightSeqMap[2].forEach(
                                                            (itemsss) => {
                                                                if (
                                                                    pointChildrenComputer[itemsss]
                                                                ) {
                                                                    pointChildrenComputer[
                                                                        itemsss
                                                                    ].push({
                                                                        id: itemss.pointId,
                                                                        name: itemss.name,
                                                                    });
                                                                } else {
                                                                    pointChildrenComputer = {
                                                                        ...pointChildrenComputer,
                                                                        [itemsss]: [
                                                                            {
                                                                                id: itemss.pointId,
                                                                                name: itemss.name,
                                                                            },
                                                                        ],
                                                                    };
                                                                }
                                                            }
                                                        );
                                                    }
                                                }
                                            });
                                        }
                                    });
                                }
                            });
                        }
                        // setPointCategoryList(task.pointCategoryList);
                        setDetail(task);
                        setPointChildrenComputer(pointChildrenComputer);
                        setPointChildrenPerson(pointChildrenPerson);
                        setLoading(false);
                    },
                    (reason) => {
                        Udesk.ui.notify.error(
                            reason.errorMsg ||
                                /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                                    .appealCheckingTask.sdk.index
                                    .interfaceRequestExceptionPleaseTryAgainLater
                        );
                        setLoading(false);
                    }
                );
            getComplainButton();
            getComplainHistory();
            setComplainPointList([]);
            timer = null;
        }, 500);
    }, [selectedTaskId]);

    const config: FormProps = {
        initialValues: {
            ...sdkOptions?.props,
            agents: hasViewAllPermission
                ? sdkOptions?.props?.agents
                : currentAgentEmail
                ? [currentAgentEmail]
                : undefined,
        },
        layout: 'inline',
        onFinish: searchListFromForm,
        customComponents: { UdeskDatePicker, GradedFilter, RatingFilter },
        fields: [
            {
                name: 'time',
                label: isTicket
                    ? /* 开始时间 */ locales.pages.appealCheckingTask.sdk.index.startTime
                    : isMixedType || isSurvey
                    ? /* 时间 */ locales.pages.appealCheckingTask.sdk.index.time
                    : appealLocales.time,
                type: 'UdeskDatePicker',
                props: {
                    defaultValue: sdkOptions?.props.defaultDateRange
                        ? sdkOptions?.props.defaultDateRange.map(
                              (i, index) =>
                                  `${i.format('YYYY-MM-DD')} ${
                                      index === 0 ? '00:00:00' : '23:59:59'
                                  }`
                          )
                        : [
                              moment().subtract(1, 'M').format('YYYY-MM-DD') + ' 00:00:00',
                              moment().format('YYYY-MM-DD') + ' 23:59:59',
                          ],
                    disabled: sdkOptions?.props.dateRangeDisabled,
                    showTime: true,
                    format: 'YYYY-MM-DD HH:mm:ss',
                },
            },
            {
                name: 'inspectTime',
                label: /* 智能质检时间 */ locales.pages.appealCheckingTask.sdk.index
                    .intelligentInspectionTime,
                type: 'UdeskDatePicker',
                props: {
                    showTime: true,
                    format: 'YYYY-MM-DD HH:mm:ss',
                },
            },
            {
                name: 'sampleTime',
                label: /* 人工质检时间 */ locales.pages.appealCheckingTask.sdk.index.manualInspectionTime,
                type: 'UdeskDatePicker',
                props: {
                    showTime: true,
                    format: 'YYYY-MM-DD HH:mm:ss',
                },
            },
            {
                name: 'externalId',
                label:
                    tasksType === Udesk.enums.inspectDataSources.voiceCall.id
                        ? appealLocales.externalIdCall
                        : isTicket
                        ? /* 工单编号 */ locales.pages.appealCheckingTask.sdk.index.workOrderNo
                        : isMixedType || isSurvey
                        ? /* 唯一记录ID */ locales.pages.appealCheckingTask.sdk.index.uniqueRecordID
                        : tasksType === Udesk.enums.inspectDataSources.videoCall.id
                        ? /* 视频会话ID */ locales.pages.appealCheckingTask.sdk.index.videoSessionID
                        : appealLocales.externalIdText,
                type: 'Input',
                props: {
                    placeholder: locales.labels.pleaseEnter,
                },
            },
            {
                name: 'callId',
                label: /* 呼叫中心通话ID */ appealLocales.callCenterCallID,
                type: 'Input',
                forceHidden: !isVoiceCall,
                props: {
                    placeholder: locales.labels.pleaseEnter,
                },
            },
            {
                name: 'dialogId',
                label: /* IM对话记录ID */ appealLocales.imDialogRecordID,
                type: 'Input',
                forceHidden: !isTextDialogue,
                props: {
                    placeholder: locales.labels.pleaseEnter,
                },
            },
            {
                name: 'customerNickName',
                label: appealLocales.customerNickName,
                type: 'Input',
                props: {
                    placeholder: locales.labels.pleaseEnter,
                },
            },
            {
                name: 'agents',
                label: /* 客服 */UdeskLocales['current'].pages.appealCheckingTask.sdk.components.qaResultView.index.customerService,
                type: 'Select',
                props: {
                    style: {
                        width: 180,
                    },
                    mode: 'multiple',
                    allowClear: true,
                    showSearch: hasViewAllPermission,
                    options: displayedAgentOptions,
                    onFocus: hasViewAllPermission ? () => searchAgents() : undefined,
                    onSearch: hasViewAllPermission ? searchAgents : undefined,
                    filterOption: false,
                    maxTagCount: 'responsive',
                    placeholder: locales.labels.pleaseSelect,
                    disabled: !hasViewAllPermission,
                },
            },
            {
                name: 'readStatus',
                label: isTicket
                    ? /* 客服已读/未读 */ locales.pages.appealCheckingTask.sdk.index
                          .customerServiceHasReadNotRead
                    : appealLocales.readStatus,
                type: 'Select',
                props: {
                    // defaultValue: sdkOptions.props.readStatus,
                    style: {
                        width: 150,
                    },
                    allowClear: true,
                    options: Udesk.enums.readStatus.map((item) => ({
                        label: item.name,
                        value: item.id,
                    })),
                    disabled: sdkOptions?.props.appealStatusDisabled,
                    placeholder: locales.labels.pleaseSelect,
                },
            },
            {
                name: 'status',
                label: appealLocales.status,
                type: 'Select',
                props: {
                    // defaultValue: sdkOptions.props.status,
                    style: {
                        width: 150,
                    },
                    allowClear: true,
                    disabled: sdkOptions?.props.appealStatusDisabled,
                    options: Udesk.enums.appealCheckStatus
                        .filter((i) =>
                            [
                                Udesk.enums.appealCheckStatus.inComplaint.id,
                                Udesk.enums.appealCheckStatus.reviewed.id,
                            ].includes(i.id)
                        )
                        .map((item) => ({ label: item.name, value: item.id })),
                    placeholder: locales.labels.pleaseSelect,
                },
            },
            {
                name: 'isManual',
                label: appealLocales.isManual,
                type: 'Select',
                props: {
                    // defaultValue: sdkOptions.props.isManual,
                    style: {
                        width: 150,
                    },
                    allowClear: true,
                    disabled: sdkOptions?.props.appealStatusDisabled,
                    options: Udesk.enums.isManual.map((item) => ({
                        label: item.name,
                        value: item.id,
                    })),
                    placeholder: locales.labels.pleaseSelect,
                },
            },
            {
                name: 'taskIds',
                label: appealLocales.taskIds,
                type: 'Select',
                props: {
                    style: {
                        width: 250,
                    },
                    mode: 'multiple',
                    allowClear: true,
                    options: taskIdsOptions,
                    onSearch: onSelectSearchTask,
                    filterOption: false,
                    maxTagCount: 'responsive',
                    placeholder: locales.labels.pleaseSelect,
                },
            },
            {
                name: 'graded',
                label: appealLocales.graded,
                type: 'GradedFilter',
                forceHidden: isTicket,
            },
            {
                name: 'rating',
                label: /* 评级 */ locales.pages.appealCheckingTask.sdk.index.grade,
                type: 'RatingFilter',
                forceHidden: isTicket,
                props: {
                    searchTasks: searchTasksInFilter,
                    inspectDataSource: tasksType,
                    sdkOptions,
                },
            },
            {
                name: 'enabledComplainFlag',
                label: /* 是否可申诉 */ locales.pages.appealCheckingTask.sdk.index.canYouAppeal,
                type: 'Select',
                props: {
                    allowClear: true,
                    options: Udesk.enums.canAppealOptions.map((item) => ({
                        label: item.name,
                        value: item.id,
                    })),
                    placeholder: locales.labels.pleaseSelect,
                },
            },
        ],
        footerRender: () => (
            <Space>
                <Button type="primary" htmlType="submit">
                    {locales.labels.query}
                </Button>
                <Button onClick={() => formRef.resetFields()}>{locales.labels.reset}</Button>
                {!isFromSdk && (
                    <Button onClick={onExport} loading={exportLoading}>
                        {locales.labels.export}
                    </Button>
                )}
            </Space>
        ),

        // onFinish: onFinish,
    };
    useEffect(() => {
        Udesk.business.sdkAjax.get('open_api_v1/user/permission', sdkOptions, { props }).then(
            (permissionResp) => {
                const permissions = permissionResp.data?.permissions || [];
                userPermissionsRef.current = permissions;
                setUserPermission(permissionResp.data || {});
                return Udesk.business.sdkAjax.get(`open_api_v1/company`, sdkOptions, { props });
            },
            (reason) => {
                Udesk.ui.notify.error(
                    reason.errorMsg ||
                        /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                            .appealCheckingTask.sdk.index
                            .interfaceRequestExceptionPleaseTryAgainLater
                );
                return Udesk.business.sdkAjax.get(`open_api_v1/company`, sdkOptions, { props });
            }
        ).then(
            (resp) => {
                const company = resp.data;
                let qualityTasksTypes: any[] = [];
                if (company.enabledCallInspect) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.voiceCall);
                }
                if (company.enabledImInspect) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.textDialogue);
                }
                if (company.enabledWeComInspect) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.wechatDialogue);
                }
                if (company.enabledWeComVoiceInspect) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.wechatRadio);
                }
                if (company.enabledTicket) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.ticket);
                }
                if (company.enableVideoInspect) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.videoCall);
                }
                // 根据后端配置，开关为true就隐藏混合类型
                if (!company.qaDataSourceFilterEnabled) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.mixedType);
                }
                
                if (company.enabledSurveyInspect) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.survey);
                }
                // if (company.enabledFeiShuInspect) {
                // qualityTasksTypes.push(Udesk.enums.inspectDataSources.feishuGroup);
                // }
                if (company.enabledMallInspect) {
                    qualityTasksTypes.push(Udesk.enums.inspectDataSources.taobao);
                }
                setQualityTasksTypes(qualityTasksTypes);
                const tasksType = qualityTasksTypes[0].id;
                setTasksType(tasksType);
                const latestCurrentAgentEmail = getCurrentAgentEmail(sdkOptions);
                const latestHasViewAll = hasSdkPermission(userPermissionsRef.current, SDK_VIEW_ALL_PERMISSION);
                formRef.setFieldsValue({
                    agents: latestHasViewAll
                        ? sdkOptions?.props?.agents
                        : latestCurrentAgentEmail
                        ? [latestCurrentAgentEmail]
                        : undefined,
                });
                const values = formRef.getFieldsValue();
                searchList({ ...values, inspectDataSource: tasksType }, true);
                const listParams = {
                    inspectDataSource: tasksType,
                };
                searchTasks(listParams, setTaskIdsOptionsFromAjax);
            },
            (reason) => {
                Udesk.ui.notify.error(
                    reason.errorMsg ||
                        /* 接口请求异常，请稍后再试 */ UdeskLocales['current'].pages
                            .appealCheckingTask.sdk.index
                            .interfaceRequestExceptionPleaseTryAgainLater
                );
                setIsInit(false);
                setLoading(false);
            }
        );
        // 初始化只执行一次，依赖项变化由查询/详情接口刷新处理
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 已终审
    const hasFinalReview = useMemo(() => detail.status === 7, [detail]);

    useImperativeHandle(
        forwardRef,
        () => {
            return {
                onTasksTypeChange,
            };
        },
        [onTasksTypeChange]
    );

    return (
        <>
            <div className="appeal-checking-task-page-filter">
                <FormBuilder key={hasViewAllPermission ? 'view-all' : 'current-agent'} {...config} form={formRef} />
            </div>
            <div className="appeal-checking-task-page-body">
                <div className="appeal-checking-task-page-left">
                    {(taskList?.length ?? 0) > 0 ? (
                        <React.Fragment>
                            <Spin spinning={listLoading}>
                                <div className="appeal-checking-task-page-left-list">
                                    <Menu selectedKeys={[`${selectedTaskId}`]} onSelect={onSelect}>
                                        {taskList.map((item) => {
                                            return (
                                                <Menu.Item key={item.dataId}>
                                                    <Card item={item.fieldValueMap} />
                                                </Menu.Item>
                                            );
                                        })}
                                    </Menu>
                                </div>
                            </Spin>
                            <div className="appeal-checking-task-page-left-page">
                                <UdeskPagination
                                    current={paging.pageNum}
                                    pageSize={paging.pageSize}
                                    total={paging.total}
                                    simple={true}
                                    showSizeChanger={false}
                                    onChange={pageChanged}
                                />
                            </div>
                        </React.Fragment>
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                </div>
                <div className="appeal-checking-task-page-right">
                    {selectedTaskId ? (
                        <React.Fragment>
                            <Alert
                                message={
                                    complainButton.inspectResultComplainTextShow === 1
                                        ? /* 当前数据不可申诉 */ UdeskLocales['current'].pages
                                              .appealCheckingTask.sdk.index
                                              .currentDataCannotBeAppealed
                                        : complainButton.complainPeriodMode ===
                                          AppealConfigModeMap.CutOff
                                        ? isNil(complainButton.permitComplainCount)
                                            ? `${complainButton.complainDeadTime}${
                                                  /* 前可申诉 */ UdeskLocales['current'].pages
                                                      .appealCheckingTask.sdk.index.beforeCanAppeal
                                              }`
                                            : Udesk.utils.string.format(
                                                  appealLocales.complainButtonInfo,
                                                  complainButton.permitComplainCount ?? 0,
                                                  complainButton.complainedCount ?? 0,
                                                  complainButton.surplusComplainCount ?? 0,
                                                  complainButton.complainDeadTime ?? 0
                                              )
                                        : Udesk.utils.string.format(
                                              appealLocales.complainButtonInfo,
                                              complainButton.permitComplainCount ?? 0,
                                              complainButton.complainedCount ?? 0,
                                              complainButton.surplusComplainCount ?? 0,
                                              complainButton.permitComplainDateTime ?? 0
                                          )
                                }
                                type="info"
                                action={
                                    <Button
                                        onClick={onSubmit}
                                        disabled={
                                            !canComplain ||
                                            !complainButton.show ||
                                            !complainPointList.length ||
                                            hasFinalReview
                                        }
                                        size="small"
                                        type="primary"
                                        loading={submitLoading}
                                    >
                                        {appealLocales.submit}
                                    </Button>
                                }
                            />

                            <InspectionDetail
                                // evaluateChange={actions.evaluateChange}
                                resetStatus={false}
                                handMarkMap={handMarkMap}
                                pointChildrenComputer={pointChildrenComputer}
                                pointChildrenPerson={pointChildrenPerson}
                                // keywords={sdkOptions.props._keywords}
                                testSetList={null}
                                testSetListFlag={false}
                                caseBaseStatus={true}
                                // onUpdate={actions.onUpdate}
                                id={detail.id}
                                // caseBase={caseBase}
                                // caseLibraryId={location.search}
                                taskId={detail.taskId}
                                // hasFeature={hasFeature}
                                detail={detail}
                                // smartTags={smartTags}
                                // changeList={changeList}
                                // qualityCheckScoreSaving={qualityCheckScoreSaving}
                                // onQualityCheckScoreCancel={actions.qualityCheckScoreCanceled}
                                // onQualityCheckScoreSave={actions.qualityCheckScoreSaved}
                                // onQualityCheckScoreSubmit={actions.qualityCheckScoreSubmitted}
                                // relatedDatas={relatedDatas}
                                // relatedDatasPaging={relatedDatasPaging}
                                // parentLoadMore={actions.parentLoadMore}
                                // relatedLoading={relatedLoading}
                                dialogRemarkList={dialogRemarkList}
                                // changeDialogRemarkList={actions.changeDialogRemarkList}
                                // querryChangeList={actions.querryChangeList}
                                // querrySmartTags={actions.querrySmartTags}
                                isWechatQa={false}
                                // changeToHighlightItemsCallDetail={related ? actions.changeCallDetail : noop}
                                // onUpdated={actions.onUpdated}
                                // refresh={actions.reloadAsyncModel}
                                // rerenderOptimization={true}
                                // requestRelatedRecords={actions.requestRelatedRecords}
                                // relatedRecordsLoading={relatedRecordsLoading}
                                loading={loading}
                                isAppeal={true}
                                canComplain={canComplain}
                                canReview={canReview}
                                isFromSdk={isFromSdk}
                                onReview={onReview}
                                // complaintList={complaintList}
                                onComplainPointListChange={onComplainPointListChange}
                                complaintHistory={complaintHistory}
                                appealSdkOptions={sdkOptions}
                                refershHistory={getComplainHistory}
                                refreshList={refreshList}
                                resetComplainPointIdMap={resetComplainPointIdMap}
                                showHit={showHit}
                                onShowHitChange={showHitChange}
                            />
                        </React.Fragment>
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                </div>
            </div>
        </>
    );
});

export default Template;
